import { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  Calendar,
  Clock,
  Users,
  MapPin,
  CheckCircle,
  XCircle,
  Package,
  CreditCard,
  Loader2,
  ChevronLeft,
  ChevronRight,
  User,
  Mail,
  Phone,
  AlertCircle,
  History,
  List,
  ChevronDown,
  X,
} from "lucide-react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Calendar as CalendarPicker } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROSTER_FILTER = {
  ALL: "all",
  CONFIRMED: "confirmed",
  PENDING_PAYMENT: "pending_payment",
  CHECKED_IN: "checked_in",
  DROP_IN_NO_SHOW: "drop_in_no_show",
  PACKAGE: "package",
};

export const AdminClassRoster = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(() => {
    const dateParam = searchParams.get("date");
    return dateParam ? parseISO(dateParam) : new Date();
  });
  const [schedules, setSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState(searchParams.get("schedule") || null);
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [rosterFilter, setRosterFilter] = useState(ROSTER_FILTER.ALL);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [page, setPage] = useState(1);
  const rosterSectionRef = useRef(null);

  useEffect(() => {
    fetchSchedules(selectedDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    if (selectedScheduleId) {
      fetchRoster(selectedScheduleId);
    } else {
      setRoster(null);
    }
  }, [selectedScheduleId]);

  useEffect(() => {
    setRosterFilter(ROSTER_FILTER.ALL);
    setPage(1);
  }, [selectedScheduleId]);

  useEffect(() => { setPage(1); }, [rosterFilter]);

  // Close overlay on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOverlayOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filteredBookings = useMemo(() => {
    const list = roster?.bookings || [];
    switch (rosterFilter) {
      case ROSTER_FILTER.CONFIRMED:
        return list.filter((b) => b.payment_status === "paid");
      case ROSTER_FILTER.PENDING_PAYMENT:
        return list.filter((b) => b.payment_status !== "paid");
      case ROSTER_FILTER.CHECKED_IN:
        return list.filter((b) => b.checked_in);
      case ROSTER_FILTER.DROP_IN_NO_SHOW:
        return list.filter((b) => b.booking_source === "drop_in" && !b.checked_in);
      case ROSTER_FILTER.PACKAGE:
        return list.filter((b) => b.booking_source === "package");
      default:
        return list;
    }
  }, [roster?.bookings, rosterFilter]);

  const filterLabels = {
    [ROSTER_FILTER.ALL]: "All bookings",
    [ROSTER_FILTER.CONFIRMED]: "Paid bookings",
    [ROSTER_FILTER.PENDING_PAYMENT]: "Pending payment",
    [ROSTER_FILTER.CHECKED_IN]: "Checked in",
    [ROSTER_FILTER.DROP_IN_NO_SHOW]: "Drop-in, not checked in yet",
    [ROSTER_FILTER.PACKAGE]: "Redeemed with package",
  };

  const selectedSchedule = schedules.find((s) => s.id === selectedScheduleId);

  const PAGE_SIZE = 10;
  const totalPages = Math.ceil(filteredBookings.length / PAGE_SIZE);
  const paginatedBookings = filteredBookings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const rosterTotalBookings = roster?.stats?.total_bookings ?? 0;
  const rosterConfirmedBookings = roster?.stats?.confirmed_bookings ?? 0;
  const rosterPendingBookings = roster?.stats?.pending_bookings ?? 0;
  const rosterCheckedIn = roster?.stats?.checked_in ?? 0;
  const rosterCheckInPct =
    rosterTotalBookings > 0 ? Math.round((rosterCheckedIn / rosterTotalBookings) * 100) : 0;

  const statCardClass = (active) =>
    `card-empire transition-all text-left w-full ${
      active ? "ring-2 ring-empire-gold/60 border-empire-gold/40" : "hover:border-neutral-600"
    }`;

  const fetchSchedules = async (date) => {
    setLoading(true);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const res = await axios.get(`${API}/admin/class-roster/schedules?date=${dateStr}`);
      setSchedules(res.data);

      const scheduleParam = searchParams.get("schedule");
      if (scheduleParam && res.data.some((s) => s.id === scheduleParam)) {
        setSelectedScheduleId(scheduleParam);
      } else if (res.data.length > 0 && !scheduleParam) {
        setSelectedScheduleId(res.data[0].id);
      } else {
        setSelectedScheduleId(null);
      }
    } catch (error) {
      console.error("Error fetching schedules:", error);
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoster = async (scheduleId) => {
    setRosterLoading(true);
    try {
      const res = await axios.get(`${API}/admin/class-roster?schedule_id=${scheduleId}`);
      setRoster(res.data);
    } catch (error) {
      console.error("Error fetching roster:", error);
      setRoster(null);
    } finally {
      setRosterLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setSearchParams({ date: format(date, "yyyy-MM-dd") });
      setCalendarOpen(false);
    }
  };

  const handleScheduleSelect = (scheduleId) => {
    setSelectedScheduleId(scheduleId);
    setSearchParams({ date: format(selectedDate, "yyyy-MM-dd"), schedule: scheduleId });

    // Scroll to roster section on mobile
    setTimeout(() => {
      if (rosterSectionRef.current && window.innerWidth < 1024) {
        rosterSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
    setSearchParams({ date: format(newDate, "yyyy-MM-dd") });
  };

  const OverlayPanel = () => (
    <AnimatePresence>
      {overlayOpen && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOverlayOpen(false)}
            className="fixed top-0 left-0 lg:left-64 right-0 bottom-0 z-30 bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            key="panel"
            initial={{ x: -340 }}
            animate={{ x: 0 }}
            exit={{ x: -340 }}
            transition={{ type: "spring", damping: 28, stiffness: 220 }}
            className="fixed top-0 left-0 lg:left-64 bottom-0 z-40 w-80 flex flex-col bg-neutral-950 border-r border-neutral-800 shadow-2xl"
          >
            {/* Currently viewing */}
            {selectedSchedule && (
              <div className="flex-shrink-0 p-4 border-b border-neutral-800 bg-neutral-900/60">
                <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-widest mb-2">
                  Currently Viewing
                </p>
                <p className="font-semibold text-white text-sm leading-snug">
                  {selectedSchedule.dance_class?.name || "Class"}
                </p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {selectedSchedule.start_datetime && (
                    <span className="text-xs text-neutral-400">
                      {format(parseISO(selectedSchedule.start_datetime), "h:mm a")}
                    </span>
                  )}
                  <Badge
                    variant="outline"
                    className={`text-[10px] px-1.5 py-0 ${
                      selectedSchedule.is_past
                        ? "border-neutral-600 text-neutral-500"
                        : "border-green-500/30 text-green-400"
                    }`}
                  >
                    {selectedSchedule.is_past ? "completed" : selectedSchedule.status}
                  </Badge>
                  <span className="text-xs text-neutral-500">
                    {selectedSchedule.current_bookings || 0} bookings
                  </span>
                </div>
              </div>
            )}

            {/* Header + close */}
            <div className="flex-shrink-0 p-3 border-b border-neutral-800">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold text-neutral-300 flex-1">
                  {format(selectedDate, "EEEE, MMM d")} · {schedules.length}{" "}
                  {schedules.length === 1 ? "class" : "classes"}
                </p>
                <button
                  onClick={() => setOverlayOpen(false)}
                  className="text-neutral-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-neutral-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Schedule list */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {schedules.map((schedule) => (
                <button
                  key={schedule.id}
                  onClick={() => { handleScheduleSelect(schedule.id); setOverlayOpen(false); }}
                  className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                    selectedScheduleId === schedule.id
                      ? "bg-empire-gold/10 border-empire-gold/40"
                      : "bg-neutral-900/50 border-neutral-800/60 hover:border-neutral-700 hover:bg-neutral-900"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className={`font-medium text-sm leading-snug ${schedule.is_past ? "text-neutral-400" : "text-white"}`}>
                      {schedule.dance_class?.name || "Class"}
                    </span>
                    {selectedScheduleId === schedule.id && (
                      <CheckCircle className="w-3.5 h-3.5 text-empire-gold flex-shrink-0 mt-0.5" />
                    )}
                  </div>
                  <div className="text-xs text-neutral-400 mt-1">
                    {schedule.start_datetime && format(parseISO(schedule.start_datetime), "h:mm a")}
                    {schedule.instructor && ` · ${schedule.instructor.name}`}
                  </div>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {schedule.current_bookings || 0}{" "}
                    {(schedule.current_bookings || 0) === 1 ? "booking" : "bookings"}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <OverlayPanel />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Class Roster</h1>
        <p className="text-neutral-400 text-sm mt-1">View bookings and manage class check-ins</p>
      </div>

      {/* Date Navigation */}
      <Card className="card-empire mb-6">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateDay(-1)}
              className="text-neutral-400 hover:text-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="min-w-[200px] justify-center border-neutral-700 hover:border-empire-gold/40"
                >
                  <Calendar className="w-4 h-4 mr-2 text-empire-gold" />
                  {format(selectedDate, "EEEE, MMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-neutral-900 border-neutral-700" align="center">
                <CalendarPicker
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateDay(1)}
              className="text-neutral-400 hover:text-white"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Class Selector */}
      {schedules.length === 0 ? (
        <Card className="card-empire">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-neutral-500" />
            <p className="text-neutral-400">No classes scheduled for this date</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop: breadcrumb trigger */}
          <div className="hidden lg:block mb-6">
            <button
              onClick={() => setOverlayOpen(true)}
              className="group flex items-center gap-1.5 px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 hover:border-empire-gold/40 hover:bg-neutral-900/80 transition-all"
            >
              <List className="w-4 h-4 text-neutral-500 group-hover:text-empire-gold transition-colors" />
              <span className="text-sm text-neutral-400">Classes</span>
              <ChevronRight className="w-3.5 h-3.5 text-neutral-600" />
              <span className="text-sm font-medium text-white">
                {selectedSchedule?.dance_class?.name || "Select a class"}
              </span>
              {selectedSchedule && (
                <Badge
                  variant="outline"
                  className={`ml-1 text-[10px] px-1.5 py-0 ${
                    selectedSchedule.is_past
                      ? "border-neutral-600 text-neutral-500"
                      : "border-green-500/30 text-green-400"
                  }`}
                >
                  {selectedSchedule.is_past ? "completed" : selectedSchedule.status}
                </Badge>
              )}
              <ChevronDown className="w-3.5 h-3.5 text-neutral-500 ml-1 group-hover:text-empire-gold transition-colors" />
            </button>
          </div>

          {/* Mobile: select dropdown */}
          <div className="lg:hidden mb-6">
            <Select value={selectedScheduleId || ""} onValueChange={handleScheduleSelect}>
              <SelectTrigger className="bg-neutral-900 border-neutral-800 text-white h-11 focus:border-empire-gold/50 focus:ring-empire-gold/20">
                <div className="flex items-center gap-2 min-w-0">
                  <Clock className="w-4 h-4 text-empire-gold flex-shrink-0" />
                  <SelectValue placeholder="Select a class..." />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-neutral-950 border-neutral-800 max-h-72">
                {schedules.map((s) => (
                  <SelectItem
                    key={s.id}
                    value={s.id}
                    className="text-white focus:bg-empire-gold/10 focus:text-empire-gold"
                  >
                    <span className="flex flex-col">
                      <span className="font-medium">{s.dance_class?.name || "Class"}</span>
                      <span className="text-xs text-neutral-400">
                        {s.start_datetime ? format(parseISO(s.start_datetime), "h:mm a") : ""}
                        {s.instructor ? ` · ${s.instructor.name}` : ""}
                        {" · "}{s.is_past ? "completed" : s.status}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Roster Details — full width */}
          <div ref={rosterSectionRef}>
            {rosterLoading ? (
              <Card className="card-empire">
                <CardContent className="py-12 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-empire-gold animate-spin" />
                </CardContent>
              </Card>
            ) : roster ? (
              <motion.div
                key={selectedScheduleId}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Past class notice */}
                {roster.schedule?.is_past && (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-neutral-700 bg-neutral-900/60 text-sm text-neutral-400">
                    <History className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                    <span>
                      This class has already taken place. The roster is <span className="text-neutral-200 font-medium">read-only</span> — analytics only.
                    </span>
                  </div>
                )}

                {/* Class Info Card */}
                <Card className="card-empire">
                  <CardContent className="py-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h2 className="text-xl font-bold text-white">
                          {roster.schedule?.dance_class?.name || "Class"}
                        </h2>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-neutral-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {roster.schedule?.start_datetime &&
                              format(parseISO(roster.schedule.start_datetime), "h:mm a")}
                            {roster.schedule?.end_datetime &&
                              ` - ${format(parseISO(roster.schedule.end_datetime), "h:mm a")}`}
                          </span>
                          {roster.schedule?.instructor && (
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {roster.schedule.instructor.name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {roster.schedule?.venue_display || "Empire Dance Studios"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Stats Row — click to filter bookings below */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <Card className={statCardClass(rosterFilter === ROSTER_FILTER.ALL)}>
                    <CardContent className="p-0">
                      <button
                        type="button"
                        onClick={() => setRosterFilter(ROSTER_FILTER.ALL)}
                        className="w-full py-3 px-2 text-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-empire-gold/50"
                        aria-pressed={rosterFilter === ROSTER_FILTER.ALL}
                      >
                        <p className="text-2xl font-bold text-white tabular-nums">{rosterTotalBookings}</p>
                        <p className="text-xs text-neutral-400">Total Bookings</p>
                        <p className="text-[10px] text-neutral-600 mt-1">Show all</p>
                      </button>
                    </CardContent>
                  </Card>
                  <Card className={statCardClass(rosterFilter === ROSTER_FILTER.CONFIRMED)}>
                    <CardContent className="p-0">
                      <button
                        type="button"
                        onClick={() => setRosterFilter(ROSTER_FILTER.CONFIRMED)}
                        className="w-full py-3 px-2 text-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-empire-gold/50"
                        aria-pressed={rosterFilter === ROSTER_FILTER.CONFIRMED}
                      >
                        <p className="text-2xl font-bold text-empire-gold tabular-nums">{rosterConfirmedBookings}</p>
                        <p className="text-xs text-neutral-400">Paid</p>
                        <p className="text-[10px] text-neutral-600 mt-1">Filter list</p>
                      </button>
                    </CardContent>
                  </Card>
                  <Card className={statCardClass(rosterFilter === ROSTER_FILTER.PENDING_PAYMENT)}>
                    <CardContent className="p-0">
                      <button
                        type="button"
                        onClick={() => setRosterFilter(ROSTER_FILTER.PENDING_PAYMENT)}
                        className="w-full py-3 px-2 text-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-empire-gold/50"
                        aria-pressed={rosterFilter === ROSTER_FILTER.PENDING_PAYMENT}
                      >
                        <p className="text-2xl font-bold text-orange-400 tabular-nums">{rosterPendingBookings}</p>
                        <p className="text-xs text-neutral-400">Pending Payment</p>
                        <p className="text-[10px] text-neutral-600 mt-1">Filter list</p>
                      </button>
                    </CardContent>
                  </Card>
                  <Card className={statCardClass(rosterFilter === ROSTER_FILTER.CHECKED_IN)}>
                    <CardContent className="p-0">
                      <button
                        type="button"
                        onClick={() => setRosterFilter(ROSTER_FILTER.CHECKED_IN)}
                        className="w-full py-3 px-2 text-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-empire-gold/50"
                        aria-pressed={rosterFilter === ROSTER_FILTER.CHECKED_IN}
                      >
                        <p className="text-2xl font-bold text-green-400 tabular-nums">
                          <span>{rosterCheckedIn}</span>
                          {rosterTotalBookings > 0 && (
                            <span className="text-sm font-semibold text-neutral-400 ml-1">
                              ({rosterCheckInPct}%)
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-neutral-400">Checked In</p>
                        <p className="text-[10px] text-neutral-600 mt-1">Filter list</p>
                      </button>
                    </CardContent>
                  </Card>
                  <Card className={statCardClass(rosterFilter === ROSTER_FILTER.DROP_IN_NO_SHOW)}>
                    <CardContent className="p-0">
                      <button
                        type="button"
                        onClick={() => setRosterFilter(ROSTER_FILTER.DROP_IN_NO_SHOW)}
                        className="w-full py-3 px-2 text-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-empire-gold/50"
                        aria-pressed={rosterFilter === ROSTER_FILTER.DROP_IN_NO_SHOW}
                      >
                        <p className="text-2xl font-bold text-amber-400 tabular-nums">
                          {roster.stats?.drop_in_no_show || 0}
                        </p>
                        <p className="text-xs text-neutral-400">Drop-In No-Shows</p>
                        <p className="text-[10px] text-neutral-600 mt-1">Drop-in, not scanned</p>
                      </button>
                    </CardContent>
                  </Card>
                  <Card className={statCardClass(rosterFilter === ROSTER_FILTER.PACKAGE)}>
                    <CardContent className="p-0">
                      <button
                        type="button"
                        onClick={() => setRosterFilter(ROSTER_FILTER.PACKAGE)}
                        className="w-full py-3 px-2 text-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-empire-gold/50"
                        aria-pressed={rosterFilter === ROSTER_FILTER.PACKAGE}
                      >
                        <p className="text-2xl font-bold text-empire-gold tabular-nums">
                          {roster.stats?.on_package || 0}
                        </p>
                        <p className="text-xs text-neutral-400">Package Redeemed</p>
                        <p className="text-[10px] text-neutral-600 mt-1">Filter list</p>
                      </button>
                    </CardContent>
                  </Card>
                </div>

                {/* Confirmed Bookings List */}
                <Card className="card-empire">
                  <CardHeader className="pb-3 space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <Users className="w-4 h-4 text-empire-gold" />
                        Bookings
                        <span className="text-neutral-400 font-normal text-sm">
                          ({filteredBookings.length}
                          {rosterFilter !== ROSTER_FILTER.ALL && roster.bookings?.length
                            ? ` of ${roster.bookings.length}`
                            : ""}
                          )
                        </span>
                      </CardTitle>
                      {rosterFilter !== ROSTER_FILTER.ALL && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-empire-gold hover:text-empire-gold/90 shrink-0 -mt-1 sm:mt-0"
                          onClick={() => setRosterFilter(ROSTER_FILTER.ALL)}
                        >
                          Clear filter
                        </Button>
                      )}
                    </div>
                    {rosterFilter !== ROSTER_FILTER.ALL && (
                      <p className="text-xs text-neutral-500">
                        Showing: <span className="text-neutral-300">{filterLabels[rosterFilter]}</span>
                        {rosterFilter === ROSTER_FILTER.DROP_IN_NO_SHOW && (
                          <span className="block mt-1 text-neutral-600">
                            Paid drop-ins who have not been scanned in yet for this class.
                          </span>
                        )}
                      </p>
                    )}
                  </CardHeader>
                  <CardContent>
                    {roster.bookings?.length === 0 ? (
                      <div className="text-center py-8 text-neutral-500">
                        <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No bookings yet</p>
                      </div>
                    ) : filteredBookings.length === 0 ? (
                      <div className="text-center py-8 text-neutral-500">
                        <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No bookings match this filter.</p>
                        <Button
                          type="button"
                          variant="link"
                          className="text-empire-gold mt-2"
                          onClick={() => setRosterFilter(ROSTER_FILTER.ALL)}
                        >
                          Show all bookings
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {paginatedBookings.map((booking) => (
                          <div
                            key={booking.booking_id}
                            className={`p-4 rounded-lg border ${booking.checked_in
                                ? "bg-green-500/5 border-green-500/20"
                                : "bg-neutral-900/50 border-neutral-800"
                              }`}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                              <div className="flex items-start gap-3">
                                <div
                                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${booking.checked_in
                                      ? "bg-green-500/20"
                                      : "bg-neutral-800"
                                    }`}
                                >
                                  {booking.checked_in ? (
                                    <CheckCircle className="w-5 h-5 text-green-400" />
                                  ) : (
                                    <User className="w-5 h-5 text-neutral-400" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-medium text-white">
                                      {booking.guest_name ||
                                        `${booking.guest_first_name || ""} ${booking.guest_last_name || ""}`.trim() ||
                                        "Guest"}
                                    </p>
                                    {booking.payment_status === "paid" ? (
                                      <Badge className="bg-empire-gold/20 text-empire-gold border-empire-gold/30 text-xs">
                                        Paid
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">
                                        Pending Payment
                                      </Badge>
                                    )}
                                    {booking.checked_in && (
                                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                                        Checked In
                                      </Badge>
                                    )}
                                    {!booking.checked_in && booking.booking_source === "drop_in" && booking.payment_status === "paid" && (
                                      <Badge
                                        variant="outline"
                                        className="border-amber-500/40 text-amber-400 text-xs"
                                      >
                                        Not checked in
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-400 mt-1">
                                    {booking.guest_email && (
                                      <span className="flex items-center gap-1">
                                        <Mail className="w-3 h-3" />
                                        {booking.guest_email}
                                      </span>
                                    )}
                                    {booking.guest_phone && (
                                      <span className="flex items-center gap-1">
                                        <Phone className="w-3 h-3" />
                                        {booking.guest_phone}
                                      </span>
                                    )}
                                  </div>
                                  {booking.booking_number && (
                                    <p className="text-xs text-neutral-500 mt-1">
                                      Ref: <span className="font-mono">{booking.booking_number}</span>
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center justify-center sm:justify-end gap-4 mt-2 sm:mt-0">
                                {booking.booking_source === "package" ? (
                                  <>
                                    <Badge
                                      className="bg-empire-gold/20 border-empire-gold/30 text-empire-gold text-xs"
                                    >
                                      <Package className="w-3 h-3 mr-1" />
                                      Package
                                    </Badge>
                                    <span className="text-xs text-neutral-500">
                                      {booking.credits_remaining} credits
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <Badge
                                      variant="outline"
                                      className="border-blue-500/30 text-blue-400 text-xs"
                                    >
                                      <CreditCard className="w-3 h-3 mr-1" />
                                      Drop-In
                                    </Badge>
                                    <span className="text-sm text-white font-medium">
                                      KES {(booking.total_amount || 0).toLocaleString()}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            {booking.booking_source === "package" && booking.package_expires_at && (
                              <div className="mt-2 pl-12 text-xs text-neutral-500">
                                Expires {format(parseISO(booking.package_expires_at), "MMM d, yyyy")}
                              </div>
                            )}
                          </div>
                        ))}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-800">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPage((p) => Math.max(1, p - 1))}
                              disabled={page === 1}
                              className="text-neutral-400 hover:text-white disabled:opacity-30"
                            >
                              <ChevronLeft className="w-4 h-4 mr-1" />
                              Prev
                            </Button>
                            <span className="text-xs text-neutral-500">
                              Page {page} of {totalPages} · {filteredBookings.length} total
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                              disabled={page === totalPages}
                              className="text-neutral-400 hover:text-white disabled:opacity-30"
                            >
                              Next
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Package Eligible Section */}
                {roster.package_eligible?.length > 0 && (
                  <Card className="card-empire">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <Package className="w-4 h-4 text-empire-gold" />
                        May Attend — Package Holders ({roster.package_eligible.length})
                      </CardTitle>
                      <p className="text-xs text-neutral-500 mt-1">
                        These guests have active packages and can check in using their package QR code.
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {roster.package_eligible.map((pkg, i) => (
                          <div
                            key={i}
                            className="p-3 rounded-lg bg-empire-gold/5 border border-empire-gold/10 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                          >
                            <div>
                              <p className="text-sm font-medium text-white">{pkg.guest_name}</p>
                              <p className="text-xs text-neutral-400">
                                {pkg.guest_email} {pkg.guest_phone && `· ${pkg.guest_phone}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 sm:text-right">
                              <Badge className="bg-empire-gold/20 border-empire-gold/30 text-empire-gold text-xs">
                                <Package className="w-3 h-3 mr-1" />
                                {pkg.package_name || "Package"}
                              </Badge>
                              <span className="text-xs text-neutral-500">
                                {pkg.credits_remaining} credits left
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Unpaid leads — filled the form, never completed payment */}
                {roster.unpaid?.length > 0 && (
                  <Card className="card-empire">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-400" />
                        Unpaid Leads ({roster.unpaid.length})
                      </CardTitle>
                      <p className="text-xs text-neutral-500">
                        Started a booking but never paid — follow up to convert
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {roster.unpaid.map((lead) => (
                          <div
                            key={lead.booking_id}
                            className="p-2.5 rounded-lg bg-yellow-500/5 border border-yellow-500/10"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm text-white">
                                {lead.guest_name || lead.guest_email}
                              </span>
                              <span className="font-mono text-xs text-empire-gold">
                                {lead.booking_number}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 mt-1 text-xs text-neutral-500">
                              {lead.guest_phone && <span>{lead.guest_phone}</span>}
                              {lead.guest_email && <span>{lead.guest_email}</span>}
                              <span className="capitalize">
                                {lead.failure_reason || lead.payment_status}
                              </span>
                              {lead.attempt_count > 1 && (
                                <span>{lead.attempt_count} attempts</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Cancelled Section */}
                {roster.cancelled?.length > 0 && (
                  <Card className="card-empire">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-400" />
                        Cancelled ({roster.cancelled.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {roster.cancelled.map((booking) => (
                          <div
                            key={booking.booking_id}
                            className="p-2 rounded-lg bg-red-500/5 border border-red-500/10 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2">
                              <XCircle className="w-4 h-4 text-red-400" />
                              <span className="text-sm text-neutral-400">
                                {booking.guest_name || booking.guest_email}
                              </span>
                            </div>
                            {booking.cancelled_at && (
                              <span className="text-xs text-neutral-500">
                                {format(parseISO(booking.cancelled_at), "MMM d, h:mm a")}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            ) : (
              <Card className="card-empire">
                <CardContent className="py-12 text-center">
                  <AlertCircle className="w-10 h-10 mx-auto mb-3 text-neutral-500" />
                  <p className="text-neutral-400">Select a class to view roster</p>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};
